/**
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { resolveCssColor } from './ColorTools.js';
/**
 * Determines which grid lines should be visible.
 *
 * 'value' turns on the grid lines along the value axis.
 * 'category' turns on the grid lines along the category axis.
 * 'both' turns on the grid lines along the value axis and the category axis.
 * 'none' turns off the grid lines along the value axis and the category axis.
 */

/**
 * Ensures that the given value is of type {@link GridVisibility}.
 *
 * INTERNAL USE ONLY
 */
export function sanitizeGridVisibility(value) {
  switch (value) {
    case 'value':
    case 'category':
    case 'both':
    case 'none':
      return value;

    default:
      // eslint-disable-next-line
      console.warn(`"${value}" is not a valid grid-visibility. "value" will be used instead.`);
      return 'value';
  }
}
/**
 * Creates a line-color provider for grids associated with value axes
 *
 * INTERNAL USE ONLY
 */

export function createValuesGridLineColorProvider(valueRange = {
  start: 0,
  end: 1
}) {
  let gridLineValue = 0;

  if (valueRange.end < 0) {
    // only negative values
    gridLineValue = valueRange.end;
  }

  if (valueRange.start > 0) {
    // only positive values
    gridLineValue = valueRange.start;
  }

  const gridLineColor = _resolveGridLineColor();

  const gridLineColorAtValue = resolveCssColor('var(--davinci-color-background-elements-3)');
  return {
    lineColor(value) {
      return value === gridLineValue ? gridLineColorAtValue : gridLineColor;
    }

  };
}
/**
 * Creates the default grid style for value axes
 *
 * INTERNAL USE ONLY
 *
 * @param gridVisibility - which grid-lines should be visible
 */

export function createValuesGridStyle(gridVisibility, valueRange = {
  start: 0,
  end: 1
}) {
  const gridStyle = {};
  gridStyle.lineColors = createValuesGridLineColorProvider(valueRange);
  gridStyle.visible = gridVisibility === 'value' || gridVisibility === 'both';
  return gridStyle;
}
/**
 * Creates a line-color provider for grids associated with category axes
 *
 * INTERNAL USE ONLY
 */

export function createCategoriesGridLineColorProvider() {
  const gridLineColor = _resolveGridLineColor();

  return {
    lineColor() {
      return gridLineColor;
    }

  };
}
/**
 * Creates the default grid style for value axes
 *
 * INTERNAL USE ONLY
 *
 * @param gridVisibility - which grid-lines should be visible
 */

export function createCategoriesGridStyle(gridVisibility) {
  const gridStyle = {};
  gridStyle.lineColors = createCategoriesGridLineColorProvider();
  gridStyle.visible = gridVisibility === 'category' || gridVisibility === 'both';
  return gridStyle;
}

function _resolveGridLineColor() {
  return resolveCssColor('var(--davinci-color-background-elements-1)');
}
